export type GetSignatureAsBase64 = () => Promise<{ dataUrl: string }>;

/**
 * Drops the inline width/height on signature images. Cover letters saved earlier carry
 * `height:30px !important` inline, which beats the export stylesheet's own !important rule and
 * printed the signature far under the required one inch. Stripping it lets the stylesheet size it.
 */
export const stripSignatureInlineSize = (html: string): string =>
  html.replace(/<img[^>]*alt="Signature"[^>]*>/gi, tag => {
    const withoutAttrs = tag.replace(/\s(?:width|height)\s*=\s*"[^"]*"/gi, '');
    return withoutAttrs.replace(/style\s*=\s*"([^"]*)"/gi, (_m, styles) => {
      const filtered = styles
        .replace(/(?:^|;)\s*(?:max-)?(?:width|height)\s*:[^;]*;?/gi, ';')
        .replace(/;{2,}/g, ';')
        .replace(/^;|;$/g, '')
        .trim();
      return filtered ? `style="${filtered}"` : '';
    });
  });

export const convertSignatureUrlToBase64 = async (
  getSignatureAsBase64: GetSignatureAsBase64,
  signatureUrl?: string,
): Promise<string | undefined> => {
  if (!signatureUrl) return undefined;
  try {
    if (signatureUrl.startsWith('data:')) return signatureUrl;
    const response = await getSignatureAsBase64();
    return response.dataUrl;
  } catch (error) {
    console.error('Failed to convert signature to base64:', error);
    return undefined;
  }
};

export const prepareHtmlWithBase64Signature = async (
  html: string,
  getSignatureAsBase64: GetSignatureAsBase64,
  signatureUrl?: string,
): Promise<string> => {
  if (!signatureUrl || !html.includes('alt="Signature"')) return html;
  try {
    const base64 = await convertSignatureUrlToBase64(getSignatureAsBase64, signatureUrl);
    if (!base64) return html.replace(/<img[^>]*alt="Signature"[^>]*>/g, '');
    return stripSignatureInlineSize(html)
      .replace(/<img([^>]*alt="Signature"[^>]*)src="[^"]*"([^>]*)>/g, `<img$1src="${base64}"$2>`) // alt before src
      .replace(/<img([^>]*)src="[^"]*"([^>]*alt="Signature"[^>]*)>/g, `<img$1src="${base64}"$2>`); // alt after src
  } catch (error) {
    console.error('Failed to prepare HTML with base64 signature:', error);
    return html.replace(/<img[^>]*alt="Signature"[^>]*>/g, '');
  }
};
